import React, { useEffect, useLayoutEffect, useState } from "react";
import DOMPurify from "dompurify";
import messageBoxIcon from "@assets/icon/message-box.svg";
import documentIcon from "@assets/icon/document.svg";
import documentIconWithDot from "@assets/icon/documentIcon-dot.svg";
import chevronDownIcon from "@assets/icon/chevron-right-grey.svg";
import downloadPurpleIcon from "@assets/icon/download-purple.svg";
import { styled } from "@linaria/react";
import Button from "../Button";
import { ProductOverview } from "./ProductOverview";
import { PlanOverview } from "./PlanOverview";
import { ITheme, themeDefault } from "src/theme-revamp";
import { useAvivaSelector } from "@redux/hook";
import { useHistory, useLocation } from "react-router-dom";
import { GAAviva } from "@utils/gaAvivaR3";
import LogoComponent from "./LogoComponent";
import { Helpers } from "@utils/helpers";
import { CIMB_Code } from "@static/campaign-code";
import { get } from "lodash";
import { CONTENT } from "@static/content";
import { PLANS_NAME } from "@static/config";
import { ROUTE_PATH } from "@static/route-paths";
import { trackOnNeedHelpStart } from "@pages/ShieldStarter/Helpers/dataAnalyticsHelpers";
import { EverGreenStyle } from "@utils/Style/everGreen";
import evergreenIcon from "@assets/icon/evergreen-plan.svg";
import evergreenGreenPlan from "@assets/icon/evergreen-plan-green.svg";
import evergreenHelpIcon from "@assets/icon/evergreen-help.svg";
import evergreenDownloadIcon from "@assets/icon/evergreen-download-brochure.svg";
import evergreenTickIcon from "@assets/icon/evergreen-tick.svg";
import { format, updateHeaderContent } from "@pages/EverGreen/Helpers/helpers";
import { css } from "@linaria/core";
import { trackOnCTA } from "@pages/CCP/Helpers/analyticsHelper";
const PromoDescription = styled.p`
  color: #434343;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 24px;
`;

const PromoFeature = styled.h3`
  margin-bottom: 8px;
  color: #434343;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
`;

const PromoBanner = styled.span`
  display: block;
  margin: 0 auto 4px 0;
  color: #f2605f;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.08em;
`;

const PromoContainer = styled.div`
  background-color: #fafafa;
  padding: 38px 24px 64px 24px;
`;

const BusinessSolutionLink = styled.a`
  color: #f2605f;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  text-decoration: underline;
  cursor: pointer;
`;

const BusinessSolutionDescription = styled.p`
  color: #434343;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
`;

const BusinessSolutions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;
  margin-bottom: 20px;
`;

const SideMenuItemText = styled.p`
  color: #434343;
  font-size: 16px;
  font-weight: 600;
  line-height: 40px;
`;

const SideMenuItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const SideMenuList = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  margin-bottom: 5px;
  padding: 0 24px 0 24px;
`;

const SideMenuCloseButton = styled.div`
  margin: 32px 0 40px auto;
  padding: 0 24px 0 24px;
  width: 32px;
  height: 32px;
  position: relative;
  cursor: pointer;
  &:before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
    width: 24px;
    height: 2px;
    background-color: #434343;
  }
  &:after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    width: 24px;
    height: 2px;
    background-color: #434343;
  }
`;

const SideMenu = styled.div<{ isShowSideMenu: boolean }>`
  box-sizing: border-box;
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: white;
  width: 100%;
  z-index: 99;
  flex-direction: column;
  align-items: stretch;
  will-change: transform;
  transition: all 0.2s linear;
  overflow: auto;
  transform: ${(props) =>
    props.isShowSideMenu ? "translateX(0)" : "translateX(100%)"};
`;

const ToggleMenuLineStack = styled.span`
  display: block;
  height: 3px;
  border-radius: 100rem;
  background-color: #434343;
`;

const ToggleMenuButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 21px;
  cursor: pointer;
  padding: 5px;
  @media screen and (min-width: 768px) {
    display: none;
  }
`;

const HeaderWrapper = styled.div<{
  isSideMenuVisible?: boolean;
  isShieldStarter?: boolean;
  isEverGreen?: boolean;
}>`
  display: block;
  position: ${(props) => (props.isShieldStarter ? "absolute" : "fixed")};
  top: 0;
  width: 100%;
  background: #ffffff;
  z-index: ${(props) => (props.isSideMenuVisible ? "130" : "100")};
  @media screen and (min-width: 768px) {
    box-shadow: 0px 2px 2px rgba(204, 204, 204, 0.5);
    position: fixed;
  }
`;

const AccountBalanceValue = styled.p<{
  weight?: string;
  color?: string;
  size?: string;
  lineHeight?: string;
}>`
  color: ${(props) => props.color || "#883e89"};
  font-size: ${(props) => props.size || "16px"};
  font-weight: ${(props) => props.weight || "600"};
  line-height: ${(props) => props.weight || "1.4"};
  letter-spacing: 0.015;
`;

const NeedHelpText = styled.p<{
  fontSize?: string;
  lineHeight?: string;
  fontColor?: string;
  fontWeight?: string;
}>`
  color: ${(props) => props.fontColor || "#8c8c8c"};
  font-size: ${(props) => props.fontSize || "16px"};
  font-weight: ${(props) => props.fontWeight || "600"};
  line-height: ${(props) => props.lineHeight || "1.4"};
  letter-spacing: 0.015;
  white-space: nowrap;
`;

const MenuIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const SideMenuIcon = styled.img`
  width: 24px;
  height: 24px;
  padding: 6px 9px 6px 9px;
  box-sizing: border-box;
  object-fit: contain;
`;

const MenuItem = styled.div`
  display: inline-flex;
  column-gap: 8px;
  align-items: center;
  position: relative;
`;

const MenuContainer = styled.div`
  display: inline-flex;
  column-gap: 24px;
  align-items: center;
  cursor: pointer;
`;

const HeaderActionsContainer = styled.div`
  display: none;
  column-gap: 50px;
  align-items: center;
  @media screen and (min-width: 768px) {
    display: inline-flex;
  }
`;

const Heading = styled.h4<{ primaryColor: string }>`
  color: ${(props) => props.primaryColor || "#883e89"};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
  padding-bottom: 8px;
`;

const SubHeading = styled.p`
  color: #8a8988;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
`;

const HeaderHeadingContainer = styled.div`
  display: none;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    display: flex;
  }
`;

const LogoContainer = styled.div<{ isSpaceBetween: boolean }>`
  display: inline-flex;
  align-items: center;
  column-gap: 25px;
  flex: 1;
  transition: all 0.2s linear;
  justify-content: ${(props) =>
    props.isSpaceBetween ? "space-between" : "unset"};
  @media screen and (min-width: 814px) {
    padding-right: ${(props) => (props.isSpaceBetween ? "24px" : "unset")};
  }
`;

const HeaderContainer = styled.div<{ isShieldStarter: boolean }>`
  display: flex;
  justify-content: space-between;
  max-width: 1200px;
  min-height: ${(prop) => (prop.isShieldStarter ? "38px" : "auto")};
  margin: auto;
  padding: 12px 12px 12px 12px;
  position: relative;
  @media screen and (min-width: 768px) {
    padding: 0 24px 0 24px;
    min-height: 86px;
  }
`;

export interface HeaderProps {
  theme?: ITheme;
}

const SideMenuNotAvialble = [
  PLANS_NAME.SHIELD_STARTER,
  PLANS_NAME.EVER_GREEN,
  PLANS_NAME.CANCER_COVER_PLUS,
];

export const Header = (props: HeaderProps) => {
  const history = useHistory();
  const CurrentPolicyFlow = useAvivaSelector(
    (state) => state.loading.CurrentPolicyFlow.policy
  );
  const sqsQuotation = useAvivaSelector((state) => {
    switch (CurrentPolicyFlow) {
      case PLANS_NAME.SHIELD_STARTER:
        return state.shieldStarterStep2.rawQuotaion;
      case PLANS_NAME.EVER_GREEN:
        return {
          summary: {
            value:
              state.everGreenStep1.quotation.sqsquotation.maturityPayout > 0 &&
              `S$${format(state.everGreenStep1.quotation.investingAmount)}`,
          },
        };
      case PLANS_NAME.CANCER_COVER_PLUS:
        return {
          summary: {
            value: state.cancerCoverStep1.quotation?.totalPremium
              ? Helpers.toCurrency(
                  state.cancerCoverStep1.quotation?.totalPremium
                )
              : undefined,
          },
        };
      default:
        return state.mltcR3Step2.sqsQuotation;
    }
  });
  const everGreenStatic = useAvivaSelector(
    (state) => state.everGreenStep1.staticData
  );
  const planOverview = useAvivaSelector((state) => {
    switch (CurrentPolicyFlow) {
      case PLANS_NAME.SHIELD_STARTER:
        return state.shieldStarterStep2.planOverview;
      case PLANS_NAME.EVER_GREEN:
        return {
          summary: {
            value:
              state.everGreenStep1.quotation.sqsquotation.maturityPayout > 0 &&
              `S$${format(state.everGreenStep1.quotation.investingAmount)}`,
          },
        };
      case PLANS_NAME.CANCER_COVER_PLUS:
        return {
          summary: {
            value: state.cancerCoverStep1.quotation?.totalPremium
              ?`S$${ Helpers.toCurrency(
                  state.cancerCoverStep1.quotation?.totalPremium
                )}`
              : undefined,
          },
        };
      default:
        return state.mltcR3Step2.planOverview;
    }
  });
  const campaignDetail = useAvivaSelector(
    (state) => state.loading.campaignDetail
  );
  const analyticUrlParams = useAvivaSelector(
    (state) => state.urlAnalytic.param
  );
  const location = useLocation();
  const currentPath: string | any = location.pathname;
  const { interestRate } = useAvivaSelector(
    (state) => state.everGreenStep1.staticData
  );
  const [isSideMenuVisible, setIsSideMenuVisible] = useState(false);
  const [onHoverHeading, setOnHoverHeading] = useState(false);
  const [onHoverPlanOverviewMenu, setOnHoverPlanOverviewMenu] = useState(false);
  const pathsNotToShowBasket = [
    ROUTE_PATH.SHIELD_STARTER_GET_QUOTE,
    ROUTE_PATH.SHIELD_STARTER_CHOOSE_PLAN,
    ROUTE_PATH.SHIELD_STARTER_GET_QUOTE + "/",
    ROUTE_PATH.SHIELD_STARTER_CHOOSE_PLAN,
    ROUTE_PATH.SHIELD_STARTER_CONTACT_US,
    ROUTE_PATH.SHIELD_STARTER_COFIRMATION,
    ROUTE_PATH.SHIELD_STARTER_GENERIC_ERROR,
    ROUTE_PATH.SHIELD_STARTER_CONNECTION_ERROR,
    ROUTE_PATH.EVER_GREEN_THANK_YOU,
    ROUTE_PATH.EVER_GREEN_GET_STARTED,
    ROUTE_PATH.EVER_GREEN_QUOTATION,
    ROUTE_PATH.CANCER_COVER_LANDING_PAGE,
    ROUTE_PATH.CANCER_COVER_BASIC_INFO,
    ROUTE_PATH.CANCER_COVER_PAYMENT_SUCCESS,
  ];

  const paramCC = campaignDetail?.campaignCode
    ? `?cc=${campaignDetail.campaignCode}`
    : "";
  const combineAnalyticParam = Helpers.getUrlAnalyticParams(
    true,
    analyticUrlParams
  );

  const content = CONTENT[CurrentPolicyFlow];

  const brouchureUrl = get(content, "brouchureUrl", "");
  const avivaBrochureUrlSS = get(window, brouchureUrl, "");
  const avivaFindOutMoreSS = get(content, "findOutMoreUrl", "");

  updateHeaderContent(content, CurrentPolicyFlow);

  const avivaBrochureUrlMltc = get(
    window,
    "AVIVA_VARIABLE_CONFIG.brochureUrlMltc"
  );
  const avivaFindOutMore =
    "https://singlife.com/en/disability-insurance/careshield";
  const travelInsuranceGetQuote = "https://singlife.com/en/travel-insurance";
  const getSingpassAppUrl =
    "https://singlife.com/en/singlife-account#singlife-account-app";

  const trackCTA = (text: string) => {
    const GA = GAAviva.getInstance();
    let { cta } = GA.digitalData;
    cta = {
      type: "Header Navigation",
      text: text,
    };
    GA.trackCTAClick({ cta });
  };

  const needHelpClick = () => {
    if (currentPath.includes("/shieldstarter/")) {
      trackOnNeedHelpStart("sss_need_help_form");
    } else if (currentPath.includes("/ccp/")) {
      console.info("Tracking Need help");
      trackOnCTA({
        ctaText: "Need Help",
        ctaType: "qb:link",
      });
    } else if (currentPath.includes("/evergreen/")) {
      //TODO add AA for evergreen
    } else {
      trackCTA("Need Help");
    }
    if (campaignDetail?.code === CIMB_Code) {
      history.push(`/mltc/contact-form` + paramCC + combineAnalyticParam);
    } else if (currentPath.includes("/shieldstarter/")) {
      history.push(ROUTE_PATH.SHIELD_STARTER_CONTACT_US);
    } else {
      if (currentPath.includes("/evergreen/")) {
        history.push(ROUTE_PATH.EVER_GREEN_CONTACTUS);
      } else {
        if (currentPath.includes("/ccp/")) {
          history.push(ROUTE_PATH.CANCER_COVER_CONTACT_US);
        } else history.push("/mltc/contact-form");
      }
    }
    setIsSideMenuVisible(false);
  };

  useEffect(() => {
    if (onHoverPlanOverviewMenu && !currentPath.includes("/ccp/")) {
      trackCTA("View Plan");
    }
  }, [onHoverPlanOverviewMenu]);

  useEffect(() => {
    console.log("sqsQuotation", sqsQuotation);
    // console.log("sqsQuotation header",sqsQuotation?.totalAftDisOriginalPremiumAmt,planOverview?.summary?.value);
  }, [sqsQuotation, planOverview]);

  const PlanOverviewMenuItem = ({ primaryColor }: any) => {
    const location: any = useLocation();
    const IS_MLTC_THANK_YOU = location.pathname.includes("/mltc/thank-you");
    const currentPathTrimmed = currentPath?.endsWith("/")
      ? currentPath?.slice(0, -1)
      : currentPath || "";
    let [viewIcon, setViewIcon] = useState({
      icon: "",
      weight: "",
      color: "",
      size: "",
      lineHeight: "",
      style: {},
    });
    useLayoutEffect(() => {
      if (currentPath?.includes("/evergreen/")) {
        viewIcon["icon"] = evergreenIcon;
        viewIcon["weight"] = "600";
        viewIcon["color"] = EverGreenStyle.colors.grey[1200];
        viewIcon["size"] = "14px";
        viewIcon["lineHeight"] = "10px";
        viewIcon["style"] = { marginTop: "5px" };
        setViewIcon({ ...viewIcon });
      }
      if (
        currentPath?.includes("/evergreen/") &&
        planOverview?.summary?.value
      ) {
        viewIcon["icon"] = evergreenGreenPlan;
        viewIcon["weight"] = "600";
        viewIcon["color"] = EverGreenStyle.colors.primary;
        viewIcon["size"] = "14px";
        viewIcon["lineHeight"] = "10px";
        viewIcon["style"] = { marginTop: "5px" };

        setViewIcon({ ...viewIcon });
      }
    }, [planOverview]);

    if (
      !IS_MLTC_THANK_YOU &&
      !pathsNotToShowBasket.includes(currentPathTrimmed)
    ) {
      return (
        <MenuItem
          onMouseEnter={() => setOnHoverPlanOverviewMenu(true)}
          onMouseLeave={() => setOnHoverPlanOverviewMenu(false)}
        >
          <>
            <MenuIcon
              src={
                location.pathname.includes("/shieldstarter/")
                  ? documentIconWithDot
                  : viewIcon.icon || documentIcon
              }
              alt="need-help?"
            />
            <AccountBalanceValue
              size={viewIcon.size}
              lineHeight={viewIcon.lineHeight}
              style={viewIcon.style}
              color={viewIcon.color}
            >
              {planOverview?.summary?.value && sqsQuotation ? (
                [ PLANS_NAME.SHIELD_STARTER].includes(CurrentPolicyFlow as PLANS_NAME)? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        `S$${planOverview?.PlanOverViewData?.subTotalAmount}`
                      ),
                    }}
                  ></span>
                ) : (
                  planOverview?.summary?.value
                )
              ) : (
                "View Plan"
              )}
            </AccountBalanceValue>
          </>

          {onHoverPlanOverviewMenu && (
            <PlanOverview
              variant="haveplan"
              primaryColor={content.primaryColor}
            />
          )}
        </MenuItem>
      );
    } else {
      return null;
    }
  };

  const LogoDynamicElement = () => {
    const campaignDetail = useAvivaSelector(
      (state) => state.loading.campaignDetail
    );
    const isSpaceBetween = campaignDetail?.code === CIMB_Code;
    const selectedPolicyFlow = useAvivaSelector(
      (state) => state.loading.CurrentPolicyFlow.policy
    );
    const { heading, primaryColor } = CONTENT[selectedPolicyFlow];
    const title = useAvivaSelector((state) => {
      if (heading.startsWith("dynamicPath")) {
        let keys = heading.split(":")[1]?.split(".") || [];
        let store: any = state;
        for (let x of keys) {
          if (store[x]) {
            store = store[x];
          }
        }
        return `${store}?`;
      }
      return "";
    });
    return (
      <LogoContainer isSpaceBetween={isSpaceBetween}>
        <LogoComponent isClickable={false} />
        <HeaderHeadingContainer
          onMouseEnter={() => setOnHoverHeading(true)}
          onMouseLeave={() => setOnHoverHeading(false)}
        >
          <SubHeading>What is</SubHeading>
          <Heading primaryColor={primaryColor || ""}>
            {title || heading}
          </Heading>
          {onHoverHeading && <ProductOverview primaryColor={primaryColor} />}
        </HeaderHeadingContainer>
      </LogoContainer>
    );
  };

  const getQuote = () => {
    window.open(travelInsuranceGetQuote, "_blank", "noopener,noreferrer");
  };
  const getSingpassApp = () => {
    window.open(getSingpassAppUrl, "_blank", "noopener,noreferrer");
  };

  const IS_SHIELD_CONTACT_FORM = location.pathname.includes(
    "/shieldstarter/contact-form"
  );
  const [isPlanOverviewDetailsOpen, setIsPlanOverviewDetailsOpen] =
    useState(false);

  let viewIcon = { icon: "", weight: "", color: "", size: "", lineHeight: "" };
  if (currentPath?.includes("/evergreen/")) {
    viewIcon["icon"] = evergreenHelpIcon;
    viewIcon["weight"] = "600";
    viewIcon["color"] = EverGreenStyle.colors.grey[1200];
    viewIcon["size"] = "14px";
    viewIcon["lineHeight"] = "unset";
  }

  return (
    <HeaderWrapper
      isSideMenuVisible={isSideMenuVisible}
      isShieldStarter={currentPath.includes("/shieldstarter/")}
      isEverGreen={currentPath.includes("/evergreen/")}
    >
      <div id="singlife-header"></div>
      <HeaderContainer
        isShieldStarter={currentPath.includes("/shieldstarter/")}
      >
        <LogoDynamicElement />
        <HeaderActionsContainer>
          <MenuContainer>
            {!IS_SHIELD_CONTACT_FORM && (
              <MenuItem>
                <MenuIcon
                  src={viewIcon.icon || messageBoxIcon}
                  alt="need-help?"
                />
                <NeedHelpText
                  fontColor={viewIcon.color}
                  fontSize={viewIcon.size}
                  lineHeight={viewIcon.lineHeight}
                  onClick={needHelpClick}
                >
                  Need help?
                </NeedHelpText>
              </MenuItem>
            )}
            <PlanOverviewMenuItem />
          </MenuContainer>
        </HeaderActionsContainer>
        <ToggleMenuButton
          onClick={() => setIsSideMenuVisible(!isSideMenuVisible)}
        >
          <ToggleMenuLineStack></ToggleMenuLineStack>
          <ToggleMenuLineStack></ToggleMenuLineStack>
          <ToggleMenuLineStack></ToggleMenuLineStack>
        </ToggleMenuButton>
      </HeaderContainer>
      <SideMenu isShowSideMenu={isSideMenuVisible}>
        <SideMenuCloseButton
          onClick={() => {
            isPlanOverviewDetailsOpen
              ? setIsPlanOverviewDetailsOpen(false)
              : setIsSideMenuVisible(false);
          }}
        ></SideMenuCloseButton>
        {!SideMenuNotAvialble.includes(CurrentPolicyFlow as PLANS_NAME) && (
          <>
            <SideMenuList>
              <SideMenuItem
                onClick={() => {
                  window.open(
                    avivaFindOutMore,
                    "_blank",
                    "noopener,noreferrer"
                  );
                  setIsSideMenuVisible(false);
                }}
              >
                <SideMenuItemText>What is Singlife CareShield</SideMenuItemText>
                <SideMenuIcon
                  src={chevronDownIcon}
                  alt="need-help?"
                ></SideMenuIcon>
              </SideMenuItem>
              <SideMenuItem
                onClick={() => {
                  window.open(
                    avivaBrochureUrlMltc,
                    "_blank",
                    "noopener,noreferrer"
                  );
                  setIsSideMenuVisible(false);
                }}
              >
                <SideMenuItemText>Product Brochure</SideMenuItemText>
                <SideMenuIcon
                  src={chevronDownIcon}
                  alt="need-help?"
                ></SideMenuIcon>
              </SideMenuItem>
              {/* <SideMenuItem>
            <SideMenuItemText>Save Quote</SideMenuItemText>
            <SideMenuIcon src={chevronDownIcon} alt="need-help?"></SideMenuIcon>
          </SideMenuItem> */}
              <SideMenuItem onClick={needHelpClick}>
                <SideMenuItemText>Contact Us</SideMenuItemText>
                <SideMenuIcon
                  src={chevronDownIcon}
                  alt="need-help?"
                ></SideMenuIcon>
              </SideMenuItem>
            </SideMenuList>
            <BusinessSolutions>
              <BusinessSolutionDescription>
                Looking for corporate solutions?
              </BusinessSolutionDescription>
              <BusinessSolutionLink>Business Solutions</BusinessSolutionLink>
            </BusinessSolutions>
            <PromoContainer>
              <PromoBanner>PROMO</PromoBanner>
              <PromoFeature>Up to 43% off Travel Insurance</PromoFeature>
              <PromoDescription>
                Save, spend, invest, earn, be insured, and rewarded all in one
                app!
              </PromoDescription>
              <Button
                onClick={getQuote}
                margin={"auto"}
                hoverBgColor={"#434343"}
                bgColor={"#434343"}
                borderColor={"#434343"}
                textColor={"#434343"}
                label="Get a Quote"
                variant="secondary"
              />
            </PromoContainer>
            <div style={{ margin: "32px 24px 32px 24px" }}>
              <Button
                onClick={getSingpassApp}
                bgColor={"#FF0008"}
                hoverBgColor={"#FF0008"}
                borderColor={"#FF0008"}
                margin={"auto"}
                variant="default"
                label="Get the Singlife App"
              />
            </div>
          </>
        )}
        {CurrentPolicyFlow === PLANS_NAME.CANCER_COVER_PLUS && (
          <>
            {!isPlanOverviewDetailsOpen && (
              <>
                <SideMenuList>
                  <SideMenuItem
                    onClick={() => {
                      setIsPlanOverviewDetailsOpen(true);
                    }}
                  >
                    <SideMenuItemText>
                      What is Singlife Cancer Cover Plus II
                    </SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                  <SideMenuItem
                    onClick={() => {
                      window.open(
                        avivaBrochureUrlSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                      setIsSideMenuVisible(false);
                    }}
                  >
                    <SideMenuItemText>Product Brochure</SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                  {/* <SideMenuItem>
            <SideMenuItemText>Save Quote</SideMenuItemText>
            <SideMenuIcon src={chevronDownIcon} alt="need-help?"></SideMenuIcon>
          </SideMenuItem> */}
                  <SideMenuItem onClick={needHelpClick}>
                    <SideMenuItemText>Contact Us</SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                </SideMenuList>
                <BusinessSolutions>
                  <BusinessSolutionDescription>
                    Looking for corporate solutions?
                  </BusinessSolutionDescription>
                  <BusinessSolutionLink
                    onClick={() => {
                      window.open(
                        "https://singlife.com/en/business",
                        "_blank",
                        "noopener,noreferrer"
                      );
                      setIsSideMenuVisible(false);
                    }}
                  >
                    Business Solutions
                  </BusinessSolutionLink>
                </BusinessSolutions>
                <PromoContainer>
                  <PromoBanner>PROMO</PromoBanner>
                  <PromoFeature>Up to 43% off Travel Insurance</PromoFeature>
                  <PromoDescription>
                    Save, spend, invest, earn, be insured, and rewarded all in
                    one app!
                  </PromoDescription>
                  <Button
                    onClick={getQuote}
                    margin={"auto"}
                    hoverBgColor={"#434343"}
                    bgColor={"#434343"}
                    borderColor={"#434343"}
                    textColor={"#434343"}
                    label="Get a Quote"
                    variant="secondary"
                  />
                </PromoContainer>
                <div style={{ margin: "32px 24px 32px 24px" }}>
                  <Button
                    onClick={getSingpassApp}
                    bgColor={"#FF0008"}
                    hoverBgColor={"#FF0008"}
                    borderColor={"#FF0008"}
                    margin={"auto"}
                    variant="default"
                    label="Get the Singlife App"
                  />
                </div>
              </>
            )}
            {isPlanOverviewDetailsOpen && (
              <SideMenuList>
                <SideMenuItem>
                  <ProductHeading>Product Overview </ProductHeading>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductHeading color={themeDefault.colors.grey[500]}>
                    What is {content.heading}
                  </ProductHeading>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductDescription>{content.description}</ProductDescription>
                </SideMenuItem>
                <SideMenuItem>
                  <div>
                    {content.features.map((entry) => (
                      <ProductBulletin text={entry} />
                    ))}
                  </div>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductDisclaimer>
                    *Premiums quoted before GST.
                  </ProductDisclaimer>
                </SideMenuItem>
                <SideMenuItem>
                  <Button
                    onClick={() => {
                      window.open(
                        avivaFindOutMoreSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    bgColor="white"
                    hoverBgColor="white"
                    hoverTextColor="#883E89"
                    textColor="#883E89"
                    width="180px"
                    label="Find out more"
                    padding="15px 30px"
                  />
                </SideMenuItem>
                <SideMenuItem
                  style={{ justifyContent: "center", marginTop: "25px" }}
                >
                  <ProductBrochure
                    onClick={() => {
                      window.open(
                        avivaBrochureUrlSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    <IconContainer>
                      <img src={downloadPurpleIcon} alt="Product Brochure" />
                    </IconContainer>
                    <ProductBrochureText>Product Brochure</ProductBrochureText>
                  </ProductBrochure>
                </SideMenuItem>
              </SideMenuList>
            )}
          </>
        )}
        {CurrentPolicyFlow === PLANS_NAME.SHIELD_STARTER && (
          <>
            {!isPlanOverviewDetailsOpen && (
              <>
                <SideMenuList>
                  <SideMenuItem
                    onClick={() => {
                      setIsPlanOverviewDetailsOpen(true);
                    }}
                  >
                    <SideMenuItemText>
                      What is Singlife Shield Starter and Health Plus
                    </SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                  <SideMenuItem
                    onClick={() => {
                      window.open(
                        avivaBrochureUrlSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                      setIsSideMenuVisible(false);
                    }}
                  >
                    <SideMenuItemText>Product Brochure</SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                  {/* <SideMenuItem>
            <SideMenuItemText>Save Quote</SideMenuItemText>
            <SideMenuIcon src={chevronDownIcon} alt="need-help?"></SideMenuIcon>
          </SideMenuItem> */}
                  <SideMenuItem onClick={needHelpClick}>
                    <SideMenuItemText>Contact Us</SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                </SideMenuList>
                <BusinessSolutions>
                  <BusinessSolutionDescription>
                    Looking for corporate solutions?
                  </BusinessSolutionDescription>
                  <BusinessSolutionLink
                    onClick={() => {
                      window.open(
                        "https://singlife.com/en/business",
                        "_blank",
                        "noopener,noreferrer"
                      );
                      setIsSideMenuVisible(false);
                    }}
                  >
                    Business Solutions
                  </BusinessSolutionLink>
                </BusinessSolutions>
                <PromoContainer>
                  <PromoBanner>PROMO</PromoBanner>
                  <PromoFeature>Up to 43% off Travel Insurance</PromoFeature>
                  <PromoDescription>
                    Save, spend, invest, earn, be insured, and rewarded all in
                    one app!
                  </PromoDescription>
                  <Button
                    onClick={getQuote}
                    margin={"auto"}
                    hoverBgColor={"#434343"}
                    bgColor={"#434343"}
                    borderColor={"#434343"}
                    textColor={"#434343"}
                    label="Get a Quote"
                    variant="secondary"
                  />
                </PromoContainer>
                <div style={{ margin: "32px 24px 32px 24px" }}>
                  <Button
                    onClick={getSingpassApp}
                    bgColor={"#FF0008"}
                    hoverBgColor={"#FF0008"}
                    borderColor={"#FF0008"}
                    margin={"auto"}
                    variant="default"
                    label="Get the Singlife App"
                  />
                </div>
              </>
            )}
            {isPlanOverviewDetailsOpen && (
              <SideMenuList>
                <SideMenuItem>
                  <ProductHeading>Product Overview </ProductHeading>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductHeading color={themeDefault.colors.grey[500]}>
                    What is {content.heading}
                  </ProductHeading>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductDescription>{content.description}</ProductDescription>
                </SideMenuItem>
                <SideMenuItem>
                  <div>
                    {content.features.map((entry) => (
                      <ProductBulletin text={entry} />
                    ))}
                  </div>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductDisclaimer>
                    *Premiums quoted before GST.
                  </ProductDisclaimer>
                </SideMenuItem>
                <SideMenuItem>
                  <Button
                    onClick={() => {
                      window.open(
                        avivaFindOutMoreSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    bgColor="white"
                    hoverBgColor="white"
                    hoverTextColor="#883E89"
                    textColor="#883E89"
                    width="180px"
                    label="Find out more"
                    padding="15px 30px"
                  />
                </SideMenuItem>
                <SideMenuItem
                  style={{ justifyContent: "center", marginTop: "25px" }}
                >
                  <ProductBrochure
                    onClick={() => {
                      window.open(
                        avivaBrochureUrlSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    <IconContainer>
                      <img src={downloadPurpleIcon} alt="Product Brochure" />
                    </IconContainer>
                    <ProductBrochureText>Product Brochure</ProductBrochureText>
                  </ProductBrochure>
                </SideMenuItem>
              </SideMenuList>
            )}
          </>
        )}
        {CurrentPolicyFlow === PLANS_NAME.EVER_GREEN && (
          <>
            {!isPlanOverviewDetailsOpen && (
              <>
                <SideMenuList>
                  <SideMenuItem
                    onClick={() => {
                      setIsPlanOverviewDetailsOpen(true);
                    }}
                  >
                    <SideMenuItemText>
                      What is {everGreenStatic.productName}
                    </SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                  <SideMenuItem
                    onClick={() => {
                      window.open(
                        avivaBrochureUrlSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                      setIsSideMenuVisible(false);
                    }}
                  >
                    <SideMenuItemText>Product Brochure</SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                  {/* <SideMenuItem>
            <SideMenuItemText>Save Quote</SideMenuItemText>
            <SideMenuIcon src={chevronDownIcon} alt="need-help?"></SideMenuIcon>
          </SideMenuItem> */}
                  <SideMenuItem onClick={needHelpClick}>
                    <SideMenuItemText>Contact Us</SideMenuItemText>
                    <SideMenuIcon
                      src={chevronDownIcon}
                      alt="need-help?"
                    ></SideMenuIcon>
                  </SideMenuItem>
                </SideMenuList>
                <BusinessSolutions>
                  <BusinessSolutionDescription>
                    Looking for corporate solutions?
                  </BusinessSolutionDescription>
                  <BusinessSolutionLink
                    onClick={() => {
                      window.open(
                        "https://singlife.com/en/business",
                        "_blank",
                        "noopener,noreferrer"
                      );
                      setIsSideMenuVisible(false);
                    }}
                  >
                    Business Solutions
                  </BusinessSolutionLink>
                </BusinessSolutions>
                <PromoContainer>
                  <PromoBanner>PROMO</PromoBanner>
                  <PromoFeature>Up to 43% off Travel Insurance</PromoFeature>
                  <PromoDescription>
                    Save, spend, invest, earn, be insured, and rewarded all in
                    one app!
                  </PromoDescription>
                  <Button
                    onClick={getQuote}
                    margin={"auto"}
                    hoverBgColor={"#434343"}
                    bgColor={"#434343"}
                    borderColor={"#434343"}
                    textColor={"#434343"}
                    label="Get a Quote"
                    variant="secondary"
                  />
                </PromoContainer>
                <div style={{ margin: "32px 24px 32px 24px" }}>
                  <Button
                    onClick={getSingpassApp}
                    bgColor={"#FF0008"}
                    hoverBgColor={"#FF0008"}
                    borderColor={"#FF0008"}
                    margin={"auto"}
                    variant="default"
                    label="Get the Singlife App"
                  />
                </div>
              </>
            )}
            {isPlanOverviewDetailsOpen && (
              <SideMenuList>
                <SideMenuItem>
                  <ProductHeading style={{ fontSize: "24px" }} color="#0A544F">
                    Product Overview{" "}
                  </ProductHeading>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductHeading
                    style={{ fontSize: "18px" }}
                    color={themeDefault.colors.grey[500]}
                  >
                    What is {everGreenStatic.productName} ?
                  </ProductHeading>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductDescription
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      height: "fit-content",
                      width: "100%",
                      color: EverGreenStyle.colors.grey[500],
                    }}
                  >
                    {content.description}
                  </ProductDescription>
                </SideMenuItem>
                <SideMenuItem>
                  <div>
                    {content.features.map((entry) => (
                      <ProductBulletin
                        className={ProductBulletinEverGreen}
                        productName={PLANS_NAME.EVER_GREEN}
                        text={entry}
                        img={evergreenTickIcon}
                      />
                    ))}
                  </div>
                </SideMenuItem>
                <SideMenuItem>
                  <ProductDisclaimer
                    style={{
                      color: EverGreenStyle.colors.grey[1200],
                      fontSize: "12px",
                      fontWeight: "500",
                      letterSpacing: "0.18px",
                      lineHeight: "19.2px",
                      marginBottom: "64px",
                    }}
                  >
                    {content.disclaimer}
                  </ProductDisclaimer>
                </SideMenuItem>
                <SideMenuItem>
                  <Button
                    onClick={() => {
                      window.open(
                        avivaFindOutMoreSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    bgColor="white"
                    hoverBgColor="white"
                    hoverTextColor="#0A544F"
                    textColor="#0A544F"
                    theme={EverGreenStyle}
                    width="180px"
                    label="Find out more"
                    padding="15px 30px"
                  />
                </SideMenuItem>
                <SideMenuItem
                  style={{ justifyContent: "center", marginTop: "25px" }}
                >
                  <ProductBrochure
                    onClick={() => {
                      window.open(
                        avivaBrochureUrlSS,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    <IconContainer>
                      <img src={evergreenDownloadIcon} alt="Product Brochure" />
                    </IconContainer>
                    <ProductBrochureText color="#0A544F">
                      Product Brochure
                    </ProductBrochureText>
                  </ProductBrochure>
                </SideMenuItem>
              </SideMenuList>
            )}
          </>
        )}
      </SideMenu>
    </HeaderWrapper>
  );
};
const ProductBrochureText = styled.p<{ color?: string }>`
  color: ${(props) => props.color || "#883e89"};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  text-decoration: underline;
`;
const ProductHeading = styled.div<{ color?: string }>`
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  display: flex;
  color: ${(props) => props.color || themeDefault.colors.primary};
`;

const ProductDescription = styled.div`
  width: 327px;
  height: 88px;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
`;

const IconContainer = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  object-fit: contain;
  box-sizing: border-box;
  img {
    width: 100%;
    height: 100%;
  }
`;
const ProductDescriptionContianer = styled.div`
  display: flex;
  font-size: 14px;
  margin-top: 16px;
  div:nth-child(2) {
    margin-left: 10px;
  }
`;

const ProductDisclaimer = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
`;

const ProductBrochure = styled.a`
  display: flex;
  column-gap: 4px;
  cursor: pointer;
`;

const ProductBulletinEverGreen = css`
  div[data-bulletins="EVER_GREEN"] {
    font-size: 14px;
    font-weight: 400;
    color: ${EverGreenStyle.colors.grey[500]};
    line-height: 19.6px;
  }
`;
const ProductBulletin = ({
  text,
  img,
  className,
  productName,
}: {
  text: string;
  img?: string;
  className?: string;
  productName?: string;
}) => {
  return (
    <ProductDescriptionContianer className={className}>
      <div style={{ width: "20px", height: "30px" }}>
        {!img && (
          <svg fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M4.53957 13.648L0.296931 9.40533C0.00403832 9.11244 0.0378136 8.60379 0.372371 8.26923C0.706928 7.93467 1.21558 7.9009 1.50847 8.19379L5.22078 11.9061L16.6617 0.465158C16.9963 0.130601 17.5049 0.0968266 17.7978 0.38972C18.0907 0.682613 18.0569 1.19126 17.7224 1.52582L5.67567 13.5725C5.34112 13.9071 4.83247 13.9409 4.53957 13.648C4.53957 13.648 4.53957 13.648 4.53957 13.648Z"
              fill={themeDefault.colors.primary}
            />
          </svg>
        )}
        {img && <img src={img} alt="tick-icon" />}
      </div>
      <div
        data-bulletins={productName}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
      ></div>{" "}
    </ProductDescriptionContianer>
  );
};
